// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import HeaderClickable from "@flip/App/Components/Header/HeaderClickable";
import MiniCart from "@flip/App/Components/MiniCart";
import { TippyPopupStyle } from "@flip/App/Components/Tippy/Style";
import TippyPopup from "@flip/App/Components/Tippy/TippyPopup";
import { xHeaderUpdate } from "@flip/App/Handlers/XNG/Header";
import { CustomAppState } from "@flip/App/Store/Reducers";
import ShoppingCart from "@flip/XDL/Icons/ShoppingCart";
import { color } from "@flip/XDL/Utilities";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import setFlyoutDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetFlyoutDrawerIsOpen";
import setNavDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetNavDrawerIsOpen";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import ShoppingBag from "@insite/mobius/Icons/ShoppingBag";
import Truck from "@insite/mobius/Icons/Truck";
import { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, withTheme } from "styled-components";

const enum fields {
    visibilityState = "visibilityState",
    icon = "icon",
}

const mapStateToProps = (state: CustomAppState) => ({
    shouldLoadCart: !getCurrentCartState(state).value && !getCurrentCartState(state).isLoading,
    totalCountDisplay: getCurrentCartState(state).value?.totalCountDisplay,
    cartUrl: getPageLinkByPageType(state, "CartPage")?.url,
    // XNG-Changes
    orderSubTotalDisplay: getCurrentCartState(state).value?.orderSubTotalDisplay,
    currencySymbol:
        getCurrentCartState(state).value?.currencySymbol || state.context.session.currency?.currencySymbol || "$",
    isTabletScreen: state.xCommon.isTabletScreen,
    isCheckoutPage:
        getCurrentPage(state).type === "CheckoutShippingPage" ||
        getCurrentPage(state).type === "CheckoutReviewAndSubmitPage",
});

const mapDispatchToProps = {
    loadCurrentCart,
    setNavDrawerIsOpen,
    setFlyoutDrawerIsOpen,
    xHeaderUpdate,
};

export interface CartLinkStyles {
    routerLink?: LinkPresentationProps;
    desktopCountText?: TypographyPresentationProps;
    mobileCountText?: TypographyPresentationProps;
    compactCountText?: TypographyPresentationProps;
    lineCount?: TypographyPresentationProps;
    tippyPopup?: TippyPopupStyle;
}

export const cartLinkStyles: CartLinkStyles = {
    routerLink: {
        typographyProps: {
            variant: "headerSecondary",
        },
        icon: {
            iconProps: {
                size: 18,
            },
        },
        color: "secondary.contrast",
        css: css`
            height: 50px;
            padding: 0 4px 0 10px;
        `,
    },
    compactCountText: {
        css: css`
            font-weight: normal;
        `,
    },
    lineCount: {
        forwardAs: "span",
        color: "common.white",
        size: 12,
        lineHeight: "24px",
        weight: "bold",
        css: css`
            background: ${color("secondary")};
            text-align: center;
            margin-right: 8px;
            height: 24px;
            border-radius: 24px;
            min-width: 24px;
        `,
    },
    tippyPopup: {
        main: {
            css: css`
                &&&&&[data-placement^="bottom"] > .tippy-arrow {
                    &::before {
                        top: 1px;
                        left: 15px !important;
                    }
                }

                &&&& > .tippy-content {
                    min-height: 290px;
                }
            `,
        },
    },
};

const iconMap = {
    cart: ShoppingCart,
    truck: Truck,
    bag: ShoppingBag,
    none: undefined,
} as const;

interface OwnProps extends WidgetProps {
    fields: {
        [fields.visibilityState]: "both" | "label" | "icon";
        [fields.icon]: keyof typeof iconMap;
    };
    isCompactHeaderCart?: boolean;
    theme: BaseTheme;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

/** Reloading the cart makes `totalCountDisplay` temporarily unavailable, so retain the previous value for better UX. */
let retainedTotalCountToDisplay: number | undefined;

const CartLink: FC<Props> = ({
    shouldLoadCart,
    cartUrl,
    totalCountDisplay,
    fields: { visibilityState, icon: selectedIcon },
    loadCurrentCart,
    isCompactHeaderCart,
    theme,
    orderSubTotalDisplay,
    currencySymbol,
    isTabletScreen,
    setNavDrawerIsOpen,
    setFlyoutDrawerIsOpen,
    isCheckoutPage,
    xHeaderUpdate,
}) => {
    const [isPopupShown, setIsPopupShown] = useState(false);

    const { isLargeTabletScreen } = useAppSelector(state => ({
        isLargeTabletScreen: (state as CustomAppState).xCommon.isLargeTabletScreen,
    }));

    useEffect(() => {
        if (shouldLoadCart) {
            loadCurrentCart({ shouldLoadFullCart: true, replaceCart: false });
        }
    }, [shouldLoadCart]);

    const styles = useMergeStyles("cartLink", cartLinkStyles);

    const showIcon = visibilityState !== "label";
    const showLabel = visibilityState !== "icon";

    if (typeof totalCountDisplay !== "undefined") {
        retainedTotalCountToDisplay = totalCountDisplay;
    }

    const icon = iconMap[(showIcon && selectedIcon) || "none"];

    const { routerLink } = styles;
    // XNG-Changes: update structure
    // if (isCompactHeaderCart) {
    //     return (
    //         <Link
    //             {...routerLink}
    //             color={theme.header.linkColor}
    //             href={cartUrl}
    //             icon={{ ...routerLink?.icon, iconProps: { src: icon, ...routerLink?.icon?.iconProps } }}
    //             data-test-selector="cartLink"
    //             aria-label="header cart count"
    //         >
    //             <Typography as="span" {...styles.compactCountText}>
    //                 {retainedTotalCountToDisplay}
    //             </Typography>
    //             <VisuallyHidden>{siteMessage("Header_CartCount")}</VisuallyHidden>
    //         </Link>
    //     );
    // }
    const closeNavigationDrawer = () => {
        setFlyoutDrawerIsOpen({ isOpen: false });
        setTimeout(() => setFlyoutDrawerIsOpen({ isOpen: undefined }), 300);
        setNavDrawerIsOpen({ navDrawerIsOpen: false });
        setTimeout(() => setNavDrawerIsOpen({ navDrawerIsOpen: undefined }), 300);
    };

    const headerClickableElement = (
        <HeaderClickable
            href={cartUrl}
            title={showLabel ? translate("Cart") : ""}
            data-test-selector="cartLink"
            iconProps={{
                src: icon,
                size: isLargeTabletScreen ? 27 : 18,
            }}
            {...{ "aria-expanded": isPopupShown }}
        >
            {retainedTotalCountToDisplay && retainedTotalCountToDisplay > 0 && (
                <Typography {...styles.lineCount} as="span">
                    {retainedTotalCountToDisplay}
                </Typography>
            )}
        </HeaderClickable>
    );

    // Stop MiniCart in Checkout pages
    if (isCheckoutPage || isTabletScreen) {
        return headerClickableElement;
    }

    return (
        <TippyPopup
            extendedStyles={styles.tippyPopup}
            content={<MiniCart isPopupShown={isPopupShown} />}
            arrow={true}
            maxWidth={472}
            placement="bottom-start"
            type="menu"
            offset={[0, 21]}
            showCloseButton={false}
            onTriggerPopupOpen={isOpen => setIsPopupShown(isOpen)}
            appendTo="parent"
            onShow={instance => {
                closeNavigationDrawer();
                xHeaderUpdate({ key: "miniCartOpened", value: true });
            }}
            onHide={instance => {
                xHeaderUpdate({ key: "miniCartOpened", value: false });
            }}
            popperOptions={{
                strategy: "absolute",
                modifiers: [
                    {
                        name: "preventOverflow",
                        options: {
                            padding: 0,
                        },
                    },
                    {
                        name: "computeStyles",
                        options: {
                            adaptive: true,
                            roundOffsets: ({ x, y }: any) => ({
                                x,
                                y,
                            }),
                        },
                    },
                ],
            }}
        >
            {headerClickableElement}
        </TippyPopup>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withTheme(CartLink)),
    definition: {
        displayName: "Cart",
        icon: "cart-shopping",
        fieldDefinitions: [
            {
                name: fields.visibilityState,
                displayName: "Settings",
                editorTemplate: "RadioButtonsField",
                options: [
                    {
                        displayName: "Show Both",
                        value: "both",
                    },
                    {
                        displayName: "Show Label Only",
                        value: "label",
                    },
                    {
                        displayName: "Show Icon Only",
                        value: "icon",
                    },
                ],
                defaultValue: "both",
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.icon,
                displayName: "Shopping Cart Icon",
                editorTemplate: "DropDownField",
                options: [
                    {
                        displayName: "None",
                        value: "none",
                    },
                    {
                        displayName: "Bag",
                        value: "bag",
                    },
                    {
                        displayName: "Cart",
                        value: "cart",
                    },
                    {
                        displayName: "Truck",
                        value: "truck",
                    },
                ],
                defaultValue: "cart",
                fieldType: "General",
                sortOrder: 2,
            },
        ],
        group: "Common",
    },
};

export default widgetModule;
